import React from "react";
import { Card, Spin, Button, Space } from "antd";
import { NumberInput } from "../components/number_input";
import { useInsuranceSettings } from "~/hooks/useInsuranceSettings";

export const InsuranceTab: React.FC = () => {
  const {
    loading,
    saving,
    hasUnsavedChanges,
    updateLocalValue,
    saveAllSettings,
    resetChanges,
    getSettingValue,
  } = useInsuranceSettings();

  const handleRateChange = (value: number) => {
    updateLocalValue("insurance_rate", value);
  };

  const handleMaxCoverageChange = (value: number) => {
    updateLocalValue("maximum_coverage", value);
  };

  const handleMinPremiumChange = (value: number) => {
    updateLocalValue("minimum_premium", value);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Insurance Settings</h3>
        <p className="text-gray-500 mb-4">
          Configure insurance rates and settings
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card title="Insurance Rate" className="w-full">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Insurance Rate (% of declared value)
              </label>
              <NumberInput
                value={getSettingValue("insurance_rate")}
                suffix="%"
                onChange={handleRateChange}
              />
              <p className="text-sm text-gray-500 mt-1">
                Percentage of declared value charged for insurance
              </p>
            </div>
          </div>
        </Card>

        <Card title="Maximum Coverage" className="w-full">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Coverage Amount
              </label>
              <NumberInput
                value={getSettingValue("maximum_coverage")}
                prefix="$"
                onChange={handleMaxCoverageChange}
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum amount covered by insurance policy
              </p>
            </div>
          </div>
        </Card>

        <Card title="Minimum Premium" className="w-full">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Insurance Premium
              </label>
              <NumberInput
                value={getSettingValue("minimum_premium")}
                prefix="$"
                onChange={handleMinPremiumChange}
              />
              <p className="text-sm text-gray-500 mt-1">
                Minimum insurance premium regardless of declared value
              </p>
            </div>
          </div>
        </Card>

        <Card title="Insurance Calculator" className="w-full">
          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-3">Current Settings Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Rate:</span>
                  <span className="ml-2 font-medium">
                    {getSettingValue("insurance_rate")}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Max Coverage:</span>
                  <span className="ml-2 font-medium">
                    ${getSettingValue("maximum_coverage").toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Min Premium:</span>
                  <span className="ml-2 font-medium">
                    ${getSettingValue("minimum_premium")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="w-full">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">
                {hasUnsavedChanges ? "Unsaved Changes" : "Insurance Settings"}
              </h4>
              <p className="text-sm text-gray-500">
                {hasUnsavedChanges
                  ? "You have unsaved changes to your insurance settings."
                  : "Save your insurance settings changes."}
              </p>
            </div>
            <Space>
              <Button
                onClick={resetChanges}
                disabled={!hasUnsavedChanges || saving}
              >
                Reset
              </Button>
              <Button
                type="primary"
                onClick={saveAllSettings}
                loading={saving}
                disabled={!hasUnsavedChanges}
              >
                Save Settings
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    </div>
  );
};
